import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api } from "../lib/api";
import { track, EVENTS } from "../lib/analytics";
import { KinvantaInlineCTA } from "./KinvantaCard";

/**
 * Reusable "Send Me a Test Call" card.
 *
 * Props:
 *   variant: "banner" | "card" | "inline"
 *   source: page identifier (e.g. "dashboard", "settings", "onboarding")
 *   defaultPhone: pre-fill phone input
 *   defaultName, defaultEmail: pre-fill modal fields for anon users
 *   compact: true to render smaller inside a sidebar card
 */
export function TestCallCard({
  variant = "card",
  source = "unknown",
  defaultPhone = "",
  defaultName = "",
  defaultEmail = "",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(defaultPhone);
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {kind:"success"|"safelist"|"rate"|"error", message}

  useEffect(() => {
    track(EVENTS.TEST_CALL_CTA_VIEWED, { source, variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => { setResult(null); setSubmitting(false); };
  const openDialog = () => { reset(); setOpen(true); };

  const submit = async () => {
    const p = (phone || "").trim();
    if (!/^\+?[0-9\s\-()]{7,24}$/.test(p)) {
      toast.error("Please enter a valid phone number with country code (e.g. +15551234567)");
      return;
    }
    setSubmitting(true);
    track(EVENTS.TEST_CALL_REQUESTED, { source, has_email: !!email });
    try {
      const { data } = await api.post("/test-call", {
        phone: p, name: name || null, email: email || null, source_page: source,
      });
      if (data.ok) {
        setResult({ kind: "success", message: data.message });
        toast.success("Dialing now — please answer the call.");
        // we optimistically fire completed after 60s so analytics lines up; real status via webhook
        setTimeout(() => track(EVENTS.TEST_CALL_COMPLETED, { source }), 60000);
      } else if (data.reason === "safelist_blocked") {
        setResult({ kind: "safelist", message: data.message });
        track(EVENTS.TEST_CALL_BLOCKED_SAFELIST, { source });
      } else if (data.reason === "rate_limited_phone" || data.reason === "rate_limited_ip") {
        setResult({ kind: "rate", message: data.message });
        track(EVENTS.TEST_CALL_FAILED, { source, reason: data.reason });
      } else {
        setResult({ kind: "error", message: data.message || "Couldn't place the test call." });
        track(EVENTS.TEST_CALL_FAILED, { source, reason: data.reason || "unknown" });
      }
    } catch (e) {
      setResult({ kind: "error", message: e?.response?.data?.detail || "Request failed." });
      track(EVENTS.TEST_CALL_FAILED, { source, reason: "network_error" });
    } finally {
      setSubmitting(false);
    }
  };

  const CTA = (
    <Button
      onClick={openDialog}
      className="rounded-full"
      data-testid={`test-call-cta-${source}`}
    >
      <PhoneCall className="h-4 w-4 mr-2" /> Send Me a Test Call
    </Button>
  );

  if (variant === "inline") {
    return (
      <>
        {CTA}
        <TestCallDialog
          open={open}
          onOpenChange={setOpen}
          {...{ phone, setPhone, name, setName, email, setEmail, submit, submitting, result, reset, source }}
        />
      </>
    );
  }

  if (variant === "banner") {
    return (
      <>
        <Card
          className="rounded-3xl border-secondary/30 bg-gradient-to-r from-secondary/10 via-accent/40 to-transparent soft-shadow"
          data-testid={`test-call-banner-${source}`}
        >
          <CardContent className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="h-11 w-11 rounded-2xl bg-secondary/20 grid place-items-center shrink-0">
              <Sparkles className="h-5 w-5 text-secondary" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-foreground">Hear CherishLink in Action</div>
              <p className="text-sm text-muted-foreground mt-1">
                Get a real 30-second demo call on your phone right now.
              </p>
            </div>
            {CTA}
          </CardContent>
        </Card>
        <TestCallDialog
          open={open}
          onOpenChange={setOpen}
          {...{ phone, setPhone, name, setName, email, setEmail, submit, submitting, result, reset, source }}
        />
      </>
    );
  }

  // default: "card"
  return (
    <>
      <Card className="rounded-3xl border-border/60 soft-shadow" data-testid={`test-call-card-${source}`}>
        <CardContent className={compact ? "p-5" : "p-6 md:p-8"}>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-2xl bg-primary/10 grid place-items-center shrink-0">
              <PhoneCall className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className={`font-display font-semibold ${compact ? "text-base" : "text-lg"}`}>
                Try a free sample call
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                We'll call you right now with a short warm demo — exactly how your loved one would hear it.
              </p>
            </div>
          </div>
          <div className="mt-5">{CTA}</div>
        </CardContent>
      </Card>
      <TestCallDialog
        open={open}
        onOpenChange={setOpen}
        {...{ phone, setPhone, name, setName, email, setEmail, submit, submitting, result, reset, source }}
      />
    </>
  );
}

function TestCallDialog({
  open, onOpenChange, phone, setPhone, name, setName, email, setEmail,
  submit, submitting, result, reset, source,
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="test-call-dialog">
        {result === null && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Hear CherishLink in action</DialogTitle>
              <DialogDescription>
                We'll call the number below with a 30-second warm demo. No spam — one-time call.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label htmlFor="tc-phone">Phone number</Label>
                <Input
                  id="tc-phone"
                  placeholder="+1 555 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="test-call-phone-input"
                  className="mt-1.5 h-11 rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1.5">Use E.164 format with country code.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="tc-name" className="text-muted-foreground text-xs">Name (optional)</Label>
                  <Input id="tc-name" value={name} onChange={(e) => setName(e.target.value)}
                         className="mt-1.5 h-10 rounded-xl" data-testid="test-call-name-input" />
                </div>
                <div>
                  <Label htmlFor="tc-email" className="text-muted-foreground text-xs">Email (optional)</Label>
                  <Input id="tc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         className="mt-1.5 h-10 rounded-xl" data-testid="test-call-email-input" />
                </div>
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="rounded-full" data-testid="test-call-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PhoneCall className="h-4 w-4 mr-2" />}
                Call me now
              </Button>
            </DialogFooter>
          </>
        )}

        {result?.kind === "success" && (
          <div className="text-center py-4" data-testid="test-call-result-success">
            <div className="h-14 w-14 rounded-full bg-secondary/15 grid place-items-center mx-auto">
              <CheckCircle2 className="h-7 w-7 text-secondary" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">We're calling you now</h3>
            <p className="mt-2 text-sm text-muted-foreground">{result.message}</p>
            <div className="mt-5">
              <KinvantaInlineCTA source={`${source}_post_testcall`} />
            </div>
            <Button onClick={() => onOpenChange(false)} className="mt-4 rounded-full" variant="outline">Close</Button>
          </div>
        )}

        {result?.kind === "safelist" && (
          <div className="py-2" data-testid="test-call-result-safelist">
            <h3 className="font-display text-lg font-semibold">Live demos fill up fast</h3>
            <p className="mt-2 text-sm text-muted-foreground">{result.message}</p>
            <div className="mt-5">
              <KinvantaInlineCTA source={`${source}_testcall_blocked`} phoneFill={phone} emailFill={email} nameFill={name} />
            </div>
          </div>
        )}

        {(result?.kind === "rate" || result?.kind === "error") && (
          <div className="py-2" data-testid={`test-call-result-${result.kind}`}>
            <h3 className="font-display text-lg font-semibold">
              {result.kind === "rate" ? "Let's try that again soon" : "Something went wrong"}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">{result.message}</p>
            <DialogFooter className="pt-4">
              <Button onClick={() => onOpenChange(false)} className="rounded-full" variant="outline">Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
